/**
 * Parsing for a reference-table cell's `format` value.
 *
 * `format` holds a CSS *selector*, not a bare class name. Design settings builds
 * it from the class and the elements the class was scoped to (`class-map.ts`,
 * `selectorsFor`):
 *
 *   elements: []      -> ".testclass"
 *   elements: ['th']  -> "th.testclass"
 *
 * plus the legacy sentinel "thead", which predates all of this and means "render
 * this cell as a <th>".
 *
 * The renderers used to derive the class with `format.replace('.', '')`. String
 * `replace` only replaces the FIRST occurrence, so an element-scoped selector
 * collapsed into one broken class: "th.testclass" -> "thtestclass". That is why a
 * custom class worked until someone scoped it to an element.
 *
 * Shared because both cell renderers need it and they sit on opposite sides of the
 * layering rules (StyledDataSheet in `shared`, DefaultTable in `app`; `shared` may
 * only import `shared`). The prose editor does the same parse inline in
 * MarkHeadingDropdown — worth folding into this helper if that is ever touched.
 */

export interface CellFormat {
  /** Element the class was scoped to ('th', 'td', 'h1'), or the 'thead' sentinel. */
  element?: string;
  /** Class name, without its leading dot. */
  className?: string;
}

export function parseCellFormat(format?: string): CellFormat {
  if (!format) return {};

  const dotIndex = format.indexOf('.');

  // ".testclass" — class only.
  if (dotIndex === 0) return { className: format.slice(1) };

  // "th.testclass" — element-scoped class. Split on the FIRST dot only, so a
  // class name containing dots is left intact.
  if (dotIndex > 0) return { element: format.slice(0, dotIndex), className: format.slice(dotIndex + 1) };

  // "thead" — no class, just the sentinel/element.
  return { element: format };
}

/**
 * The class a cell should carry for its format, '' when it has none.
 *
 * A dotless value falls back to itself so the long-standing `thead` class keeps
 * being emitted: nothing in either repo styles `.thead`, but a customer's own CSS
 * might, and dropping a class is riskier than keeping one. This is also what makes
 * the two renderers agree — DefaultTable previously gated on `includes('.')` and
 * emitted nothing here.
 */
export function cellFormatClassName(format?: string): string {
  const { element, className } = parseCellFormat(format);
  return className ?? element ?? '';
}

/**
 * Tags a table cell can actually be. Anything else in an element-scoped selector
 * keeps the default `td`.
 *
 * This whitelist is the point. The prose editor's equivalent switch enumerates the
 * tags it can materialise (h1-h6) and falls through to a default for anything
 * else, while still applying the class — which is how a selector's element can be
 * silently ignored. A cell cannot be an `h1`, so `h1.ingress` applies the class to
 * a `td` and the `h1.ingress` rule simply will not match. Offering such options
 * for cells is a dropdown concern, not something this can rescue.
 */
const CELL_TAGS = new Set(['td', 'th']);

/**
 * Which tag a cell should render as, given its `format` selector.
 *
 * `thead` is handled explicitly: it is a legacy sentinel meaning "this cell is a
 * header", not an element name. Treating it as one would emit a `<thead>` inside a
 * `<tr>`, which is invalid.
 */
export function cellFormatTag(format?: string): 'td' | 'th' {
  const { element } = parseCellFormat(format);

  if (!element) return 'td';
  if (element === 'thead') return 'th';

  return CELL_TAGS.has(element) ? (element as 'td' | 'th') : 'td';
}

export interface CellStylingSource {
  format?: string;
  classes?: unknown;
}

export interface CellStyling {
  isHead: boolean;
  /** The cell element a selector names ('td' / 'th'); undefined for anything else. */
  element?: 'td' | 'th';
  classes: string[];
}

const listedClasses = (classes: unknown): string[] =>
  Array.isArray(classes) ? classes.filter((c): c is string => typeof c === 'string' && c.trim() !== '') : [];

const cellElement = (element?: string): 'td' | 'th' | undefined => (element && CELL_TAGS.has(element) ? (element as 'td' | 'th') : undefined);

/**
 * A cell's element lives in `format` ('thead', or a legacy `th.x` selector) and its
 * classes in `classes[]`. Legacy rows kept their class inside `format` too, so both
 * are read.
 */
export function resolveCellStyling(cell?: CellStylingSource): CellStyling {
  const { element, className } = parseCellFormat(cell?.format);

  return {
    isHead: cellFormatTag(cell?.format) === 'th',
    element: cellElement(element),
    classes: [...new Set([...(className ? [className] : []), ...listedClasses(cell?.classes)])],
  };
}

/** A toolbar selector as a cell option: `.sum`, `td.td-75px` or a bare `th`. */
export function parseCellOption(selector?: string): { element?: 'td' | 'th'; className?: string } {
  const { element, className } = parseCellFormat(selector);
  return { element: cellElement(element), className };
}

/** Every class a cell renders with: its format's class (legacy `thead` included) plus `classes[]`. */
export function cellClassNames(cell?: CellStylingSource): string {
  return [...new Set([cellFormatClassName(cell?.format), ...listedClasses(cell?.classes)])].filter(Boolean).join(' ');
}
